<script setup>
import SelectOptionFactory from '@/modules/select_option/SelectOptionFactory.js'
import SelectOptionVuetify from '@/modules/select_option/SelectOptionVuetify.js'
import { translate } from '@/plugins/i18n.js'
import { debounce } from '@/services/helpers/UtilityHelper'
import { ValidationHelper } from '@/services/helpers/ValidationHelper'
import SiteConfig from '@/services/SiteConfig'
import { computed, reactive, ref, toRaw, watch } from 'vue'

const props = defineProps({
  initialAddress: Object
})

const emits = defineEmits(['updateData'])

const setInitialAddress = () => {
  model.title = props.initialAddress?.title
  model.gender = props.initialAddress?.gender
  model.first_name = props.initialAddress?.first_name
  model.last_name = props.initialAddress?.last_name
  model.company = props.initialAddress?.company
  model.additional = props.initialAddress?.additional
  model.street_name = props.initialAddress?.street_name
  model.street_number = props.initialAddress?.street_number
  model.zip = props.initialAddress?.zip
  model.city = props.initialAddress?.city
  model.country = props.initialAddress?.country || SiteConfig.locale
}

const model = reactive({})
setInitialAddress()

const genderRef = ref(null)
const firstNameRef = ref(null)
const lastNameRef = ref(null)
const companyRef = ref(null)
const additionalRef = ref(null)
const streetNameRef = ref(null)
const streetNumberRef = ref(null)
const zipRef = ref(null)
const cityRef = ref(null)

const titles = SelectOptionFactory.getTitleOptions()

const genderRules = [(value) => !!value || translate('validation_msg_required')]
const firstNameRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]
const lastNameRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]
const streetNameRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]
const streetNumberRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 10 || translate('validation_msg_max_length', 10)
]
const zipRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 10 || translate('validation_msg_max_length', 10)
]
const cityRule = [
  (value) => !!value || translate('validation_msg_required'),
  (value) => value.length <= 50 || translate('validation_msg_max_length', 50)
]

const defaultCountry = ref(
  new SelectOptionVuetify(SiteConfig.locale, translate(SiteConfig.locale))
)

const form = ref(false)
const formRef = ref(null)

const emitUpdate = debounce((newValue) => {
  emits('updateData', form.value ? toRaw(newValue) : null)
}, 100)

watch(
  model,
  (newValue) => {
    emitUpdate(newValue)
  },
  { deep: true }
)

const validate = async (resetValidation) => {
  const { valid } = await formRef.value.validate()
  if (resetValidation) {
    formRef.value.resetValidation()
  }
  return valid
}

const hasPackstation = computed(() => ValidationHelper.hasPackstation(model))

const companyHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.company)
)

const additionalHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.additional)
)

const streetNameHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.street_name)
)

const firstNameHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.first_name)
)

const lastNameHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.last_name)
)

const cityHasPackstation = computed(() =>
  ValidationHelper.hasPackstationKeyword(model.city)
)

defineExpose({
  validate,
  isValid: form.value,
  reset: () => setInitialAddress(),
  getModel: () => toRaw(model)
})
</script>

<template>
  <VForm ref="formRef" v-model="form">
    <div class="v-row">
      <div class="v-col-12 v-col-sm-6">
        <VRadioGroup
          ref="genderRef"
          v-model="model.gender"
          :rules="genderRules"
          class="pt-0"
          inline
        >
          <VRadio
            :label="translate('gender_female_label')"
            value="female"
            :ripple="false"
          />
          <VRadio
            :label="translate('gender_male_label')"
            value="male"
            :ripple="false"
          />
        </VRadioGroup>
      </div>
      <div class="v-col-12 v-col-sm-6">
        <VSelect
          :items="titles"
          :label="translate('opc_address_title_label')"
          v-model="model.title"
          autocomplete="honorific-prefix"
          outlined
          clearable
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="firstNameRef"
          :aria-label="translate('opc_address_firstname_label')"
          :label="translate('opc_address_firstname_label')"
          :rules="firstNameRule"
          v-model="model.first_name"
          required
          autocomplete="given-name"
          name="given-name"
          :error-messages="
            firstNameHasPackstation
              ? translate('packstation_disclaimer')
              : undefined
          "
          :append-inner-icon="
            firstNameHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="lastNameRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="lastNameRef"
          :aria-label="translate('opc_address_lastname_label')"
          :label="translate('opc_address_lastname_label')"
          :rules="lastNameRule"
          v-model="model.last_name"
          autocomplete="family-name"
          name="family-name"
          :error-messages="
            lastNameHasPackstation
              ? translate('packstation_disclaimer')
              : undefined
          "
          :append-inner-icon="
            lastNameHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="companyRef?.focus()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="companyRef"
          :aria-label="translate('opc_address_company_label')"
          :label="translate('opc_address_company_label')"
          v-model="model.company"
          autocomplete="organization"
          name="organization"
          :error-messages="
            companyHasPackstation
              ? translate('packstation_disclaimer')
              : undefined
          "
          :append-inner-icon="
            companyHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="additionalRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-6">
        <VTextField
          ref="additionalRef"
          :aria-label="translate('opc_address_additional_label')"
          :label="translate('opc_address_additional_label')"
          v-model="model.additional"
          autocomplete="address-line2"
          name="address-line2"
          :error-messages="
            additionalHasPackstation
              ? translate('packstation_disclaimer')
              : undefined
          "
          :append-inner-icon="
            additionalHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="streetNameRef?.focus()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-8">
        <VTextField
          ref="streetNameRef"
          :aria-label="translate('opc_address_street_name_label')"
          :label="translate('opc_address_street_name_label')"
          :rules="streetNameRule"
          v-model="model.street_name"
          autocomplete="address-line1"
          name="address-line1"
          :error-messages="
            streetNameHasPackstation
              ? translate('packstation_disclaimer')
              : undefined
          "
          :append-inner-icon="
            streetNameHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="streetNumberRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-4">
        <VTextField
          ref="streetNumberRef"
          :aria-label="translate('opc_address_street_number_label')"
          :label="translate('opc_address_street_number_label')"
          :rules="streetNumberRule"
          v-model="model.street_number"
          autocomplete="off"
          name="street-number"
          @keydown.enter="zipRef?.focus()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-12 v-col-sm-4">
        <VTextField
          ref="zipRef"
          :aria-label="translate('opc_address_zip_label')"
          :label="translate('opc_address_zip_label')"
          :rules="zipRule"
          v-model="model.zip"
          autocomplete="postal-code"
          name="postal-code"
          outlined
          @keydown.enter="cityRef?.focus()"
        />
      </div>
      <div class="v-col-12 v-col-sm-8">
        <VTextField
          ref="cityRef"
          :aria-label="translate('opc_address_city_label')"
          :label="translate('opc_address_city_label')"
          :rules="cityRule"
          v-model="model.city"
          autocomplete="address-level2"
          name="address-level2"
          :error-messages="
            cityHasPackstation ? translate('packstation_disclaimer') : undefined
          "
          :append-inner-icon="
            cityHasPackstation ? 'custom:infoFilled' : undefined
          "
          @keydown.enter="cityRef?.blur()"
        />
      </div>
    </div>
    <div class="v-row">
      <div class="v-col-7 v-col-sm-6">
        <VSelect
          :items="[defaultCountry]"
          :aria-label="translate('opc_address_country_label')"
          :label="translate('opc_address_country_label')"
          v-model="model.country"
          autocomplete="country"
          name="country"
          disabled
        />
      </div>
      <div class="v-col-5 v-col-sm-6 text-right">
        {{ translate('opc_mandatory_label') }}
      </div>
    </div>
  </VForm>
</template>
